import gql from 'graphql-tag';
import { IMarket, ISelection } from '../../types';

export const fragmentSuperParlayBetLegs = gql`
  fragment BetLegs on Multiple {
    betLegs {
      legRefs
      price {
        d
        f
        a
      }
      type
    }
  }
`;

/**
 * IVAPIBettingOpportunities must mirror the fields returned by this fragment
 */
export const fragmentBettingOpportunities = gql`
  fragment opportunities on BettingOpportunities {
    singles {
      id
      marketId
      eventId
      eachWay
      type
      stakePerLine
      potentialReturns
      numOfLines
      priceType
      buyingPointsAllowed
      line
      requestedLine
      betWithRewardCreditsAllowed
      price {
        decimal
        fractional
        american
      }
      originalPrice {
        decimal
        fractional
        american
      }
      errors {
        resource
        pointer
        code
      }
    }
    parlays {
      id
      type
      typeName
      eachWay
      stakePerLine
      potentialReturns
      numOfLines
      betWithRewardCreditsAllowed
      legs {
        selectionId
      }
      isSuperParlay
      ...BetLegs
      price {
        decimal
        fractional
        american
      }
    }
    roundRobins {
      id
      eachWay
      numOfLines
      stakePerLine
      potentialReturns
      type
      typeName
      betWithRewardCreditsAllowed
      price {
        decimal
        fractional
        american
      }
    }
    teasers {
      eachWay
      type
      name
      potentialReturns
      teaserSelections
      teaserLevel
      stakePerLine
      betWithRewardCreditsAllowed
      levels {
        level
        points
      }
      price {
        decimal
        fractional
        american
      }
    }
    errors {
      code
      message
      details
      resource
      pointer
      field
    }
  }

  ${fragmentSuperParlayBetLegs}
`;

// TODO: Convert these to fragments

export const getBet = () => `
  id
  type
  typeName
  numLines
  status
  placedAt
  placedBy {
    id
  }
  currency
  totalStake
  stakePerLine
  rewardCreditsPerLine
  rewardCreditsStakePerLine
  placedAtLocation {
    venueId
    deviceType
    slotNumber
  }
  potentialReturns
  originalPotentialReturns
  toWin
  originalToWin
  price {
    d
    f
    a
  }
  estimatedOdds {
    d
    f
    a
  }
  originalPrice {
    d
    f
    a
  }
  showVoidedAdjustmentMessage
  payout
  payoutExpiresAt
  payOutType
  paidOut
  settled
  retailTicketId
  playerCardId
  blockPayout
  boxed
  cashOut
  betLegs {
    legRefs
    price {
      d
      f
      a
    }
    originalPrice {
      d
      f
      a
    }
    type
  }
  legs {
    id
    type
    legNumber
    eventStartTime
    event {
      id
      name
      raceNumber
    }
    sport {
      id
      name
    }
    market {
      id
      name
    }
    competition {
      id
      name
    }
    selection {
      id
      name
      selectionType
      runners {
        number
        name
      }
    }
    price {
      d
      f
      a
    }
     originalPrice {
      d
      f
      a
    }
    priceType
    marketLine
    requestedLine
    result {
      type
    }
  }
  extensions {
    reprints {
      timestamp
    }
    money {
      questions {
        id
        text
        title
      }
      taxes {
        applied {
          amount
          rate
        }
        no {
          amount
          rate
        }
        questionId
        taxType
        yes {
          amount
          rate
        }
      }
      type
    }
    cashOut {
      enabled
      available
      availabilityReason
      amount
    }
    receipt {
      qrCodeEnabled
      qrCodeUrl
    }
  }
`;

export type GetPrimaryMarketsVariables = {
  includeHidden: boolean;
  primaryMarkets: string[];
};

/**
 * @deprecated use PrimaryMarkets fragment.
 */
export const getPrimaryMarkets = (full = false) => `
  marketField: marketsByName(selectedMarkets: $primaryMarkets, includeHidden: $includeHidden) {
    id
    marketCode
    name
    status
    ancestors
    hcapValue
    displayed
    byoEligible
    ${
      full
        ? `
      specialConditions
    betLimits {
      maxBet
    }
    metadata {
      marketCategory
      marketCategoryDisplayTitle
      team
      player
    }
      `
        : ''
    }

    ${getSelections()}
  }
`;

export const selectionsFragment = gql`
  fragment SelectionsFields on Selection {
    ancestors
    currentPriceDen
    currentPriceNum
    currentPriceDecimal
    displayed
    fbResult
    id
    name
    status
    metadata {
      team
      player
    }
  }
`;

export const primaryMarketsFragment = gql`
  fragment PrimaryMarkets on Market {
    id
    name
    status
    ancestors
    hcapValue
    displayed
    byoEligible
    selections {
      ...SelectionsFields
    }
  }

  ${selectionsFragment}
`;

export const getFilteredMarkets = () => `
marketField: marketsByName(selectedMarkets: $selectedMarkets, marketNames: $marketNames, includeHidden: $includeHidden, preferredOnly: $preferredOnly) {
  id
  name
  status
  ancestors
  hcapValue
  displayed
  metadata {
    team
    player
  }
  ${getSelections()}
}
`;

const selectionFields = [
  'ancestors',
  'currentPriceDen',
  'currentPriceNum',
  'currentPriceDecimal',
  'displayed',
  'fbResult',
  'id',
  'name',
  'status'
] as const;

export type SelectionFromQuery = Pick<ISelection, (typeof selectionFields)[number] | 'metadata'>;

export const getSelections = ({ includeTypeName }: { includeTypeName: boolean } = { includeTypeName: false }) => `
  selections {
    ${selectionFields.join(' ')}
    metadata {
      team
      player
    }
    ${includeTypeName ? '__typename' : ''}
  }
`;

export const MovingLinesFragment = gql`
  fragment MovingLines on Market {
    movingLines {
      sportId
      marketId
      eventId
      competitionId
      displayed
      status
      cashOutAvailable
      minPricedLine
      maxPricedLine
      lineIncrement
      linePrices {
        line
        displayed
        status
        cashOutAvailable
        selections {
          selectionType
          currentPriceNum
          currentPriceDen
        }
      }
    }
  }
`;

export const eventFieldsFragment = gql`
  fragment EventFields on Event {
    id
    name
    status
    competitionId
    competitionName
    competitionDisplayOrder
    ancestors
    displayed
    isInPlay
    startDateTime
    teams {
      name
      fbResult
    }
    metadata {
      currentHomeStartingPitcher
      currentAwayStartingPitcher
    }
  }
`;

// Deprecate this in favour of the above.
export const getEvent = () => `
  id
  name
  status
  competitionId
  competitionName
  ancestors
  displayed
  isInPlay
  startDateTime
  teams {
    name
    fbResult
  }
  metadata {
    currentHomeStartingPitcher
    currentAwayStartingPitcher
  }
`;

export const marketFieldsFragment = gql`
  fragment MarketFields on Market {
    id
    marketCode
    name
    templateName
    status
    ancestors
    hcapValue
    displayed
    byoEligible
    specialConditions
    marketTemplateDisplayOrder
    collectionDisplayOrder
  }
`;

// Deprecate these in favour of the above.
export const getMarket = () => `
  id
  name
  templateName
  status
  ancestors
  hcapValue
  displayed
  byoEligible
  specialConditions
`;

export const getMarkets = () => `
  marketField: markets {
    ${marketGraphql}
  }
`;

const marketFields = [
  'ancestors',
  'byoEligible',
  'displayed',
  'hcapValue',
  'id',
  'name',
  'parentId',
  'status'
] as const;

export type MarketFromQuery = Pick<IMarket, (typeof marketFields)[number]> & { selections: SelectionFromQuery[] };
export type MarketField = MarketFromQuery & { selections: SelectionFromQuery[] };

export const marketGraphql = `
  ${marketFields.join(' ')}
  ${getSelections()}
`;

export const fragmentSuperParlayBetLegsV2 = gql`
  fragment BetLegsV2 on MultipleV2 {
    betLegs {
      legRefs
      price {
        decimal
        fractional
        american
      }
      type
    }
  }
`;
export const fragmentBettingOpportunitiesV2 = gql`
  fragment opportunitiesV2 on BettingOpportunitiesV2 {
    singles {
      id
      marketId
      eventId
      eachWay
      type
      stakePerLine
      potentialReturns
      numOfLines
      priceType
      buyingPointsAllowed
      line
      requestedLine
      betWithRewardCreditsAllowed
      bettable
      price {
        decimal
        fractional
        american
      }
      originalPrice {
        decimal
        fractional
        american
      }
      errors {
        resource
        pointer
        code
      }
    }
    parlays {
      id
      type
      typeName
      eachWay
      stakePerLine
      potentialReturns
      numOfLines
      betWithRewardCreditsAllowed
      bettable
      legs {
        selectionId
        marketId
        eventId
        buyingPointsAllowed
        included
      }
      isSuperParlay
      ...BetLegsV2
      price {
        decimal
        fractional
        american
      }
      errors {
        errorStatus
        parlayStatus
        details {
          message
          combinationErrorDetails {
            description
            reasons {
              description
              legs {
                selectionId
              }
            }
            errorStatus
          }
        }
      }
    }
    roundRobins {
      id
      eachWay
      numOfLines
      stakePerLine
      potentialReturns
      type
      typeName
      betWithRewardCreditsAllowed
      bettable
      price {
        decimal
        fractional
        american
      }
    }
    teasers {
      eachWay
      type
      name
      potentialReturns
      teaserSelections
      teaserLevel
      stakePerLine
      betWithRewardCreditsAllowed
      bettable
      levels {
        level
        points
      }
      price {
        decimal
        fractional
        american
      }
    }
    errors {
      code
      message
      details
      resource
      pointer
      field
    }
  }

  ${fragmentSuperParlayBetLegsV2}
`;

export const KeyMarketGroupsWithPartialsFragment = gql`
  fragment KeyMarketGroupsWithPartials on Event {
    keyMarketGroups(tabId: $tabId, secondaryTabId: $secondaryTabId) {
      marketDisplayTypeId
      marketDisplayGroupDisplayName
      isOddsBoost
      columnHeaders {
        name
        hasSGP
      }
      marketDisplayRows {
        displayName
        selectionIds
        selections {
          id
          displayName
        }
        team
        imageUrl
      }
      marketField: partialMarkets {
        id
        contextId
      }
      teams
      useMarketHeader
    }
  }
`;

export const KeyMarketGroupsFragment = gql`
  fragment KeyMarketGroups on Event {
    keyMarketGroups(tabId: $tabId, secondaryTabId: $secondaryTabId) {
      marketDisplayTypeId
      marketDisplayGroupDisplayName
      isOddsBoost
      columnHeaders {
        name
        hasSGP
      }
      marketDisplayRows {
        displayName
        selectionIds
        selections {
          id
          displayName
        }
        team
        imageUrl
      }
      marketField: markets {
        id
        name
        displayed
        status
        marketCode
        ancestors
        hcapValue
        contextId
        byoEligible
        ...Metadata
        ...Selections
        ...MovingLines
        specialConditions
        betLimits {
          maxBet
        }
      }
      teams
      useMarketHeader
    }
  }
`;

export const MetadataFragment = gql`
  fragment Metadata on Market {
    metadata {
      marketCategory
      marketCategoryDisplayTitle
      team
      player
    }
  }
`;

export const SelectionsFragment = gql`
  fragment Selections on Market {
    selections {
      ancestors
      currentPriceDen
      currentPriceNum
      currentPriceDecimal
      displayed
      fbResult
      id
      name
      status
      metadata {
        team
        player
      }
    }
  }
`;

export const TabsFragment = gql`
  fragment TabData on Tab {
    id
    displayName
    __typename
  }
`;
